import { FC, ReactNode, useEffect } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import classNames from 'classnames';
import { ArrowBackButton, CustomButton, FormTextField, InfoHintModalAnchor } from 'components/shared';
import { IPaymentPayerFormData } from 'utils/types';
import { paymentPayerValidationSchema } from 'utils/validation';
import styles from './index.module.scss';

interface PaymentPayerFormProps {
	instructionNumber?: string | number;
	instructionDate?: string;
	initialValues: IPaymentPayerFormData;
	onSubmit: (data: IPaymentPayerFormData) => void | Promise<void>;
	onBack?: () => void;
	isLoading?: boolean;
	submitLabel?: string;
	isLastNameFirst?: boolean;
	showUltimateDebtor?: boolean;
	isEmbedded?: boolean;
	title?: string | null;
	titleHint?: ReactNode;
	titleHintAnalyticsPage?: string;
	titleHintAnalyticsButton?: string;
	formId?: string;
	isHideSubmitButton?: boolean;
	onValidityChange?: (isValid: boolean) => void;
}

export const PaymentPayerForm: FC<PaymentPayerFormProps> = ({
	instructionNumber,
	instructionDate,
	initialValues,
	onSubmit,
	onBack,
	isLoading = false,
	submitLabel = 'Продовжити',
	isLastNameFirst = false,
	showUltimateDebtor = true,
	isEmbedded = false,
	title = 'Дані платника',
	titleHint,
	titleHintAnalyticsPage,
	titleHintAnalyticsButton,
	formId,
	isHideSubmitButton = false,
	onValidityChange,
}) => {
	const methods = useForm<IPaymentPayerFormData>({
		defaultValues: initialValues,
		resolver: yupResolver(paymentPayerValidationSchema),
		mode: 'onChange',
	});
	const {
		control,
		handleSubmit,
		trigger,
		formState: { isValid, isSubmitting },
	} = methods;
	const isPayerSameAsUltimateDebtor = useWatch({ control, name: 'isPayerSameAsUltimateDebtor' });

	const isFormLoading = isLoading || isSubmitting;

	useEffect(() => {
		trigger();
	}, [trigger]);

	useEffect(() => {
		onValidityChange?.(isValid);
	}, [isValid, onValidityChange]);

	return (
		<div className={classNames(styles.wrapper, { [styles.wrapper_embedded]: isEmbedded })}>
			{onBack && <ArrowBackButton className={styles.back} onClick={onBack} />}
			{title && (
				<div className={styles.title}>
					{!!titleHint && !!titleHintAnalyticsPage && !!titleHintAnalyticsButton && (
						<InfoHintModalAnchor
							className={styles.title__hint}
							analyticsPage={titleHintAnalyticsPage}
							analyticsButton={titleHintAnalyticsButton}
						>
							{titleHint}
						</InfoHintModalAnchor>
					)}
					<Typography variant="h5">{title}</Typography>
				</div>
			)}

			{(instructionNumber !== undefined || instructionDate) && (
				<div className={styles.instruction}>
					{instructionNumber !== undefined && (
						<div className={styles.instruction__item}>
							<Typography variant="body2" color="var(--color-neutral-500)">
								Номер платіжної інструкції
							</Typography>
							<Typography variant="body1">{instructionNumber}</Typography>
						</div>
					)}
					{instructionDate && (
						<div className={styles.instruction__item}>
							<Typography variant="body2" color="var(--color-neutral-500)">
								Дата
							</Typography>
							<Typography variant="body1">{instructionDate}</Typography>
						</div>
					)}
				</div>
			)}

			<FormProvider {...methods}>
				<form id={formId} className={styles.form} onSubmit={handleSubmit(onSubmit)}>
					<section className={styles.section}>
						<Typography variant="subtitle1">Платник</Typography>
						<div className={styles.nameFields}>
							{isLastNameFirst ? (
								<>
									<FormTextField<IPaymentPayerFormData> name="lastName" label="Прізвище" />
									<FormTextField<IPaymentPayerFormData> name="firstName" label="Ім’я" />
								</>
							) : (
								<>
									<FormTextField<IPaymentPayerFormData> name="firstName" label="Ім’я" />
									<FormTextField<IPaymentPayerFormData> name="lastName" label="Прізвище" />
								</>
							)}
							<FormTextField<IPaymentPayerFormData> name="middleName" label="По батькові" />
						</div>
						<FormTextField<IPaymentPayerFormData>
							name="taxId"
							label="ІПН (РНОКПП)"
							inputProps={{ inputMode: 'numeric', maxLength: 10 }}
						/>
					</section>

					{showUltimateDebtor && (
						<Controller
							control={control}
							name="isPayerSameAsUltimateDebtor"
							render={({ field }) => (
								<FormControlLabel
									className={styles.checkbox}
									label="Платник збігається з фактичним платником"
									control={
										<Checkbox
											checked={field.value}
											onBlur={field.onBlur}
											onChange={(_, checked) => field.onChange(checked)}
										/>
									}
								/>
							)}
						/>
					)}

					{showUltimateDebtor && !isPayerSameAsUltimateDebtor && (
						<section className={styles.section}>
							<Typography variant="subtitle1">Фактичний платник</Typography>
							<div className={styles.nameFields}>
								{isLastNameFirst ? (
									<>
										<FormTextField<IPaymentPayerFormData> name="ultimateDebtor.lastName" label="Прізвище" />
										<FormTextField<IPaymentPayerFormData> name="ultimateDebtor.firstName" label="Ім’я" />
									</>
								) : (
									<>
										<FormTextField<IPaymentPayerFormData> name="ultimateDebtor.firstName" label="Ім’я" />
										<FormTextField<IPaymentPayerFormData> name="ultimateDebtor.lastName" label="Прізвище" />
									</>
								)}
								<FormTextField<IPaymentPayerFormData> name="ultimateDebtor.middleName" label="По батькові" />
							</div>
							<FormTextField<IPaymentPayerFormData>
								name="ultimateDebtor.taxId"
								label="ІПН (РНОКПП)"
								inputProps={{ inputMode: 'numeric', maxLength: 10 }}
							/>
						</section>
					)}

					{!isHideSubmitButton && (
						<CustomButton
							type="submit"
							label={submitLabel}
							disabled={!isValid || isFormLoading}
							isLoading={isFormLoading}
						/>
					)}
				</form>
			</FormProvider>
		</div>
	);
};
